import * as React from 'react';
import {
  FaRegQuestionCircle,
  FaUsersCog,
  FaQuestion,
  FaPhone,
  FaUser,
  FaSignOutAlt,
  FaChartPie,
  FaMailBulk,
  FaChartLine,
  FaHeadset,
  FaBars,
  FaTimes,
  FaArrowRight,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { PoseGroup } from 'react-pose';
import { Switch, Route } from 'react-router';
import { cssRaw } from 'typestyle';
import { RouteContainer } from '../Common/RouteContainer';
import { Question } from '../master/question/Question';
import { Questionnaire } from '../master/questionnaire/Questionnaire';
import { AssignWork } from '../assignWork/AssignWork';
import { Calling } from '../Calling';
import { UserDetail } from '../User/UserDetail';
import { UserConsumer } from '../Common/UserContext';
import { UserService } from '../../service/userService';
import { Email } from '../Email/Email';
import { SummarisedCallHistory } from '../Report/SummarisedCallHistory';
import { DetailCallHistoryReport } from '../Report/DetailCallHistoryReport';
import { RetailerReport } from '../Report/RetailerReport/RetailerReport';
import { RetailerQandA } from '../Report/RetailerReport/RetailerQandA';
import { CallAnalytics } from '../Report/CallAnalytics';

// require(), not import: tsconfig is commonjs with no esModuleInterop and
// file-loader exports `module.exports = "<url>"` with no `.default`, so an
// ESM-style import silently yields undefined instead of a path.
const logoMark = require('../../assets/img/logo-mark.png');
const homeBg = require('../../assets/img/home-bg.png');

/**
 * Role gating is expressed as predicates rather than a role whitelist so it
 * stays byte-identical to the previous markup -- including the behaviour while
 * `userType` is still undefined during the bootstrap query, where
 * `['SUPERVISOR','CALLER'].indexOf(undefined) === -1` is true.
 */
const ADMIN_ONLY = (t: string) => ['SUPERVISOR', 'CALLER'].indexOf(t) === -1;
const NOT_CALLER = (t: string) => t !== 'CALLER';
const ANYONE = () => true;

interface NavItem {
  to: string;
  label: string;
  icon: JSX.Element;
  show: (userType: string) => boolean;
  blurb: string;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard/start_calling/', label: 'Start Calling', icon: <FaPhone />, show: ANYONE,
    blurb: 'Work through your allocated retailers' },
  { to: '/dashboard/assign_work/', label: 'Assign Work', icon: <FaUsersCog />, show: NOT_CALLER,
    blurb: 'Allocate towns and areas to callers' },
  { to: '/dashboard/user_detail/', label: 'User Detail', icon: <FaUser />, show: ADMIN_ONLY,
    blurb: 'Manage users, roles and coverage' },
  { to: '/dashboard/question/', label: 'Question', icon: <FaQuestion />, show: ADMIN_ONLY,
    blurb: 'Author the questions callers ask' },
  { to: '/dashboard/questionnaire/', label: 'Questionnaire', icon: <FaRegQuestionCircle />, show: ADMIN_ONLY,
    blurb: 'Group questions into a live script' },
  { to: '/dashboard/call_history_report/', label: 'Call History', icon: <FaChartPie />, show: ADMIN_ONLY,
    blurb: 'Calls answered, missed and retried' },
  { to: '/dashboard/retailer_report/', label: 'Retailer Report', icon: <FaChartLine />, show: ADMIN_ONLY,
    blurb: 'Per-retailer outcomes and answers' },
  { to: '/dashboard/call_analytics/', label: 'Call Analytics', icon: <FaHeadset />, show: NOT_CALLER,
    blurb: 'Calls and recordings imported from Diallogs' },
  { to: '/dashboard/email/', label: 'Email Setting', icon: <FaMailBulk />, show: ADMIN_ONLY,
    blurb: 'Who receives the completion report' },
];

/**
 * Segment-aware active match. A plain `startsWith` would light up "Question"
 * while sitting on "Questionnaire", since one path is a prefix of the other.
 */
const isActive = (pathname: string, to: string) => {
  const here = (pathname || '').replace(/\/+$/, '');
  const target = to.replace(/\/+$/, '');
  return here === target || here.indexOf(target + '/') === 0;
};

cssRaw(`
  .cs-shell {
    min-height: 100vh;
    background: linear-gradient(180deg, #F4F8FE 0%, #EDF3FB 100%);
  }

  /* ---------- top navigation ---------- */
  .cs-nav {
    position: sticky;
    top: 0;
    z-index: 60;
    background: rgba(255,255,255,0.82);
    backdrop-filter: blur(16px) saturate(1.5);
    -webkit-backdrop-filter: blur(16px) saturate(1.5);
    border-bottom: 1px solid rgba(15,37,71,0.08);
    box-shadow: 0 2px 20px rgba(15,37,71,0.05);
  }
  .cs-nav__inner {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 0 clamp(14px, 3vw, 30px);
    height: 62px;
  }

  .cs-nav__brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
    flex: 0 0 auto;
    padding: 6px 8px 6px 2px;
    border-radius: 12px;
    transition: transform .2s ease;
  }
  .cs-nav__brand:hover { transform: translateY(-1px); }
  .cs-nav__brand img { width: 34px; height: 34px; display: block; }
  .cs-nav__brand-text { display: flex; flex-direction: column; line-height: 1.05; }
  .cs-nav__brand-name {
    font-size: 14.5px; font-weight: 800; letter-spacing: .10em; color: #0F2547;
  }
  .cs-nav__brand-sub {
    font-size: 8.5px; font-weight: 700; letter-spacing: .22em;
    color: #7C93B5; text-transform: uppercase;
  }

  .cs-nav__links {
    display: flex;
    align-items: center;
    gap: 0;
    flex: 1 1 auto;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: none;
  }
  .cs-nav__links::-webkit-scrollbar { display: none; height: 0; }

  /* Eight items at the original 13px padding / 13.5px type measured 1017px wide
     against 918px of available space at a 1440 viewport, so the last item was
     clipped. Tightened to ~870px, which cleared 1440 with the name shown.

     Call Analytics made it nine. Measured against the deployed CSS in a
     browser: eight items are 860px, nine are 974px (Call Analytics is 113px).
     The strip is overflow-x:auto with the scrollbar hidden, so exceeding the
     space does not look clipped -- the last item is simply unreachable.

     Everything outside the strip costs a fixed ~440px, plus ~118px for the
     name, so the horizontal layout needs a viewport of ~1414px with the name
     hidden. The breakpoints below are set from those measurements, not
     estimated: the name drops at 1600 (needing 1532 to fit with it shown) and
     the strip collapses to the toggle at 1430, which leaves 17px of margin at
     1431 and keeps the bar horizontal at 1440. */
  .cs-nav__link {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 9px 8px;
    border-radius: 10px;
    color: #45587C;
    font-size: 12.5px;
    font-weight: 600;
    letter-spacing: .012em;
    text-decoration: none;
    white-space: nowrap;
    transition: color .18s ease, background-color .18s ease;
  }
  .cs-nav__link svg {
    font-size: 14px;
    opacity: .82;
    transition: transform .2s ease, opacity .18s ease;
  }
  .cs-nav__link:hover { color: #0F2547; background-color: rgba(30,111,217,0.09); }
  .cs-nav__link:hover svg { transform: translateY(-1.5px) scale(1.08); opacity: 1; }

  /* Underline wipes in from the left on hover, and stays put when active. */
  .cs-nav__link::after {
    content: '';
    position: absolute;
    left: 8px; right: 8px; bottom: 3px;
    height: 2px;
    border-radius: 2px;
    background: linear-gradient(90deg, #38BDF8 0%, #1E40AF 100%);
    transform: scaleX(0);
    transform-origin: left center;
    transition: transform .24s cubic-bezier(.4,0,.2,1);
  }
  .cs-nav__link:hover::after { transform: scaleX(1); }

  .cs-nav__link--active {
    color: #0B3D91;
    background-color: rgba(30,111,217,0.12);
  }
  .cs-nav__link--active svg { opacity: 1; }
  .cs-nav__link--active::after { transform: scaleX(1); }

  .cs-nav__right { display: flex; align-items: center; gap: 10px; flex: 0 0 auto; }

  .cs-nav__role {
    display: inline-flex;
    align-items: center;
    height: 26px;
    padding: 0 11px;
    border-radius: 999px;
    font-size: 10.5px;
    font-weight: 800;
    letter-spacing: .13em;
    color: #fff;
    background: linear-gradient(135deg, #0A6BA8 0%, #1E40AF 100%);
    box-shadow: 0 3px 10px rgba(14,64,175,0.28);
    white-space: nowrap;
  }
  .cs-nav__who {
    font-size: 12.5px; font-weight: 600; color: #52658A;
    max-width: 150px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }

  .cs-nav__logout {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 8px 13px;
    border: 1px solid rgba(15,37,71,0.12);
    border-radius: 10px;
    background: rgba(255,255,255,0.7);
    color: #52658A;
    font-size: 13px; font-weight: 600; font-family: inherit;
    cursor: pointer;
    transition: color .18s ease, border-color .18s ease, background-color .18s ease, transform .18s ease;
  }
  .cs-nav__logout svg { transition: transform .2s ease; }
  .cs-nav__logout:hover {
    color: #B4232A;
    border-color: rgba(180,35,42,0.4);
    background-color: rgba(180,35,42,0.07);
    transform: translateY(-1px);
  }
  .cs-nav__logout:hover svg { transform: translateX(2px); }

  .cs-nav__toggle {
    display: none;
    align-items: center; justify-content: center;
    width: 38px; height: 38px;
    border: 1px solid rgba(15,37,71,0.12);
    border-radius: 10px;
    background: rgba(255,255,255,0.7);
    color: #0F2547; font-size: 16px; cursor: pointer;
    transition: background-color .18s ease, transform .18s ease;
  }
  .cs-nav__toggle:hover { background-color: rgba(30,111,217,0.10); transform: translateY(-1px); }

  /* Visible keyboard focus everywhere in the bar. */
  .cs-nav a:focus-visible,
  .cs-nav button:focus-visible {
    outline: 2px solid #2563EB;
    outline-offset: 2px;
  }

  /* ---------- content ---------- */
  .cs-main { padding: 18px clamp(12px, 2.4vw, 26px) 40px; }
  .cs-main--bare { padding: 0; }

  /* ---------- home ---------- */
  .cs-home {
    position: relative;
    min-height: calc(100vh - 62px);
    display: flex;
    align-items: center;
    padding: 44px clamp(18px, 5vw, 76px);
    background-image: url(${homeBg});
    background-size: cover;
    background-position: center right;
    background-repeat: no-repeat;
    overflow: hidden;
  }
  /* The artwork is busy on the right and open on the left, so the copy sits
     left and this wash keeps it legible without hiding the illustration. */
  .cs-home::before {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(100deg,
      rgba(255,255,255,0.94) 0%,
      rgba(255,255,255,0.78) 36%,
      rgba(255,255,255,0.20) 60%,
      rgba(255,255,255,0) 76%);
    pointer-events: none;
  }
  .cs-home__inner { position: relative; width: 100%; max-width: 620px; }

  .cs-home__eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 5px 12px 5px 8px;
    border-radius: 999px;
    background: rgba(255,255,255,0.8);
    border: 1px solid rgba(15,37,71,0.09);
    box-shadow: 0 4px 14px rgba(15,37,71,0.06);
    font-size: 10.5px; font-weight: 800; letter-spacing: .16em;
    color: #2C5488; text-transform: uppercase;
    margin-bottom: 18px;
  }
  .cs-home__eyebrow img { width: 20px; height: 20px; display: block; }

  .cs-home h1 {
    margin: 0 0 10px;
    font-size: clamp(28px, 4.2vw, 44px);
    line-height: 1.1;
    font-weight: 800;
    letter-spacing: -0.02em;
    color: #0F2547;
  }
  .cs-home h1 em {
    font-style: normal;
    background: linear-gradient(120deg, #0A6BA8 0%, #1E40AF 100%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
  .cs-home__lead {
    margin: 0 0 26px;
    font-size: 15px;
    line-height: 1.6;
    color: #46587B;
    max-width: 46ch;
  }

  .cs-home__quick {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 12px;
  }
  .cs-tile {
    position: relative;
    display: block;
    padding: 15px 16px;
    border-radius: 14px;
    text-decoration: none;
    background: rgba(255,255,255,0.78);
    border: 1px solid rgba(15,37,71,0.09);
    box-shadow: 0 4px 16px rgba(15,37,71,0.05);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    overflow: hidden;
    transition: transform .22s cubic-bezier(.4,0,.2,1),
                box-shadow .22s ease,
                border-color .22s ease,
                background-color .22s ease;
  }
  /* Accent rail wipes down the left edge on hover. */
  .cs-tile::before {
    content: '';
    position: absolute; left: 0; top: 0; bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #38BDF8 0%, #1E40AF 100%);
    transform: scaleY(0);
    transform-origin: top center;
    transition: transform .26s cubic-bezier(.4,0,.2,1);
  }
  .cs-tile:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 32px rgba(15,37,71,0.12);
    border-color: rgba(30,111,217,0.28);
    background-color: rgba(255,255,255,0.92);
  }
  .cs-tile:hover::before { transform: scaleY(1); }
  .cs-tile:focus-visible { outline: 2px solid #2563EB; outline-offset: 3px; }

  .cs-tile__head { display: flex; align-items: center; gap: 10px; margin-bottom: 5px; }
  .cs-tile__icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 32px; height: 32px; flex: 0 0 auto;
    border-radius: 9px;
    background: linear-gradient(135deg, rgba(56,189,248,0.16) 0%, rgba(30,64,175,0.16) 100%);
    color: #1553A8; font-size: 14px;
    transition: transform .22s ease;
  }
  .cs-tile:hover .cs-tile__icon { transform: scale(1.08) rotate(-4deg); }
  .cs-tile__title {
    font-size: 14.5px; font-weight: 700; color: #0F2547; letter-spacing: .005em;
    flex: 1 1 auto;
  }
  .cs-tile__go {
    font-size: 12px; color: #7C93B5;
    opacity: 0; transform: translateX(-4px);
    transition: opacity .22s ease, transform .22s ease;
  }
  .cs-tile:hover .cs-tile__go { opacity: 1; transform: translateX(0); }
  .cs-tile__blurb { margin: 0; font-size: 12.5px; line-height: 1.5; color: #5A6E92; }

  /* ---------- responsive ---------- */
  /* Drop the name first -- it buys ~160px and is the least useful item in the
     bar, since the role badge still identifies the session. */
  @media (max-width: 1600px) {
    .cs-nav__who { display: none; }
  }
  @media (max-width: 1430px) {
    .cs-nav__toggle { display: inline-flex; }
    .cs-nav__inner { gap: 12px; }
    .cs-nav__links {
      position: absolute;
      top: 62px; left: 0; right: 0;
      flex-direction: column;
      align-items: stretch;
      gap: 3px;
      padding: 10px;
      /* Opaque, not 0.97: at 3% bleed a 44px heading behind the panel still
         ghosted through legibly, which read as a rendering fault. */
      background: #FFFFFF;
      border-bottom: 1px solid rgba(15,37,71,0.10);
      box-shadow: 0 14px 30px rgba(15,37,71,0.10);
      overflow: hidden;
      max-height: 0;
      opacity: 0;
      pointer-events: none;
      transition: max-height .28s ease, opacity .2s ease, padding .28s ease;
    }
    .cs-nav__links--open {
      max-height: 70vh;
      opacity: 1;
      pointer-events: auto;
      overflow-y: auto;
    }
    /* Roomier hit targets once the links are a stacked panel. */
    .cs-nav__link { padding: 12px 14px; font-size: 14.5px; }
    .cs-nav__link::after { display: none; }
  }

  /* Phone-sized only. The hero keeps its side-by-side composition on a 1280
     laptop; it is only below ~900 that the artwork has to move behind the copy. */
  @media (max-width: 900px) {
    .cs-nav__logout span { display: none; }
    .cs-nav__logout { padding: 8px 11px; }
    .cs-home { min-height: calc(100vh - 62px); padding: 34px 20px; background-position: 72% center; }
    .cs-home::before {
      background: linear-gradient(175deg,
        rgba(255,255,255,0.93) 0%,
        rgba(255,255,255,0.86) 55%,
        rgba(255,255,255,0.62) 100%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .cs-nav__link, .cs-nav__link svg, .cs-nav__link::after,
    .cs-nav__logout, .cs-nav__logout svg, .cs-nav__toggle,
    .cs-nav__brand, .cs-tile, .cs-tile::before, .cs-tile__icon, .cs-tile__go {
      transition: none !important;
    }
  }
`);

interface DashboardState {
  menuOpen: boolean;
}

export class Dashboard extends React.Component<{}, DashboardState> {
  constructor(props: {}) {
    super(props);
    this.state = { menuOpen: false };
  }

  closeMenu = () => this.setState({ menuOpen: false });

  toggleMenu = () => this.setState((s) => ({ menuOpen: !s.menuOpen }));

  render() {
    const { menuOpen } = this.state;

    return (
      <UserConsumer>
        {(user) => {
          const userType = user && user.userType;
          const name = user && user.employee && user.employee.name;
          const items = NAV_ITEMS.filter((i) => i.show(userType));

          return (
            <Route
              render={({ location }) => (
                <div className='cs-shell'>
                  <nav className='cs-nav'>
                    <div className='cs-nav__inner'>
                      <Link to='/dashboard' className='cs-nav__brand' onClick={this.closeMenu}>
                        <img src={logoMark} alt='' aria-hidden='true' />
                        <span className='cs-nav__brand-text'>
                          <span className='cs-nav__brand-name'>CALLING SOUL</span>
                          <span className='cs-nav__brand-sub'>Tele Pro</span>
                        </span>
                      </Link>

                      <div
                        className={'cs-nav__links' + (menuOpen ? ' cs-nav__links--open' : '')}
                        id='cs-nav-links'
                      >
                        {items.map((item) => (
                          <Link
                            key={item.to}
                            to={item.to}
                            onClick={this.closeMenu}
                            className={
                              'cs-nav__link' +
                              (isActive(location.pathname, item.to) ? ' cs-nav__link--active' : '')
                            }
                            aria-current={isActive(location.pathname, item.to) ? 'page' : undefined}
                          >
                            {item.icon}
                            {item.label}
                          </Link>
                        ))}
                      </div>

                      <div className='cs-nav__right'>
                        {name && <span className='cs-nav__who'>{name}</span>}
                        {userType && <span className='cs-nav__role'>{userType}</span>}
                        <button
                          type='button'
                          className='cs-nav__logout'
                          onClick={() => new UserService().logout()}
                        >
                          <span>Logout</span>
                          <FaSignOutAlt />
                        </button>
                        <button
                          type='button'
                          className='cs-nav__toggle'
                          onClick={this.toggleMenu}
                          aria-expanded={menuOpen}
                          aria-controls='cs-nav-links'
                          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                        >
                          {menuOpen ? <FaTimes /> : <FaBars />}
                        </button>
                      </div>
                    </div>
                  </nav>

                  <main className={'cs-main' + (location.pathname.replace(/\/+$/, '') === '/dashboard' ? ' cs-main--bare' : '')}>
                    <PoseGroup>
                      {/*
                        Keyed on the path, NOT Math.random(). A random key gives the
                        node a new identity on every render, so React unmounts and
                        remounts the whole route -- refetching every query and
                        resetting every form. That was survivable while Dashboard was
                        stateless; with the menu toggle it would wipe in-progress work
                        on every open. PoseGroup wants the path key anyway, so exit
                        animations now actually run.
                      */}
                      <RouteContainer key={location.pathname}>
                        <Switch location={location}>
                          <Route path='/dashboard/call_history_report/:id' component={DetailCallHistoryReport} />
                          <Route path='/dashboard/call_history_report' component={SummarisedCallHistory} />
                          <Route path='/dashboard/retailer_report/:id' component={RetailerQandA} />
                          <Route path='/dashboard/retailer_report' component={RetailerReport} />
                          <Route path='/dashboard/call_analytics' component={CallAnalytics} />
                          <Route path='/dashboard/user_detail' component={UserDetail} />
                          <Route path='/dashboard/question' component={Question} />
                          <Route path='/dashboard/questionnaire' component={Questionnaire} />
                          <Route path='/dashboard/assign_work' component={AssignWork} />
                          <Route path='/dashboard/email/' component={Email} />
                          <Route path='/dashboard/start_calling/' component={Calling} />
                          <Route exact path='/dashboard' component={Home} />
                        </Switch>
                      </RouteContainer>
                    </PoseGroup>
                  </main>
                </div>
              )}
            />
          );
        }}
      </UserConsumer>
    );
  }
}

const Home = () => (
  <UserConsumer>
    {(user) => {
      const userType = user && user.userType;
      const name = user && user.employee && user.employee.name;
      const items = NAV_ITEMS.filter((i) => i.show(userType));

      return (
        <section className='cs-home'>
          <div className='cs-home__inner'>
            <span className='cs-home__eyebrow'>
              <img src={logoMark} alt='' aria-hidden='true' />
              Tele Pro Workspace
            </span>

            <h1>
              {name ? <>Welcome back, <em>{name}</em></> : <>Welcome to <em>Calling Soul</em></>}
            </h1>
            <p className='cs-home__lead'>
              Your call centre workspace for Minu Marketing &mdash; allocate work, dial retailers,
              capture responses and track every outcome in one place.
            </p>

            <div className='cs-home__quick'>
              {items.map((item) => (
                <Link key={item.to} to={item.to} className='cs-tile'>
                  <span className='cs-tile__head'>
                    <span className='cs-tile__icon'>{item.icon}</span>
                    <span className='cs-tile__title'>{item.label}</span>
                    <FaArrowRight className='cs-tile__go' />
                  </span>
                  <p className='cs-tile__blurb'>{item.blurb}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      );
    }}
  </UserConsumer>
);
